// frontend/src/pages/checkout/CheckoutShell.tsx
//
// 托管收银台外壳：顶部品牌区 + 内容 + 底部信任标识。
// 完全独立于商户后台布局（不含侧边栏 / 顶栏）。
//
// 新增：右上角轻量「语言切换器 + 明暗切换按钮」。
// - 语言 / 主题 state 由上层（CheckoutSessionFlow）统一管理并通过 props 传入，
//   外壳只负责渲染切换控件并回调；这样卡字段重挂等副作用都在上层集中处理。
// - data-theme 属性挂在最外层 .fs-checkout 容器上，暗色 CSS 用 [data-theme="dark"] 选择器覆盖。

import React from 'react';
import { LockOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';
import { CheckoutLang, CheckoutTheme, t, LANG_OPTIONS } from './i18n';
import './checkout.css';

interface CheckoutShellProps {
  children: React.ReactNode;
  /** 商户名（有则展示「由 {商户名} 提供」） */
  merchantName?: string;
  /** 商户 logo URL（有则展示在品牌区） */
  logoUrl?: string;
  /** 是否隐藏品牌区（如 embedded 弹层模式下可隐藏冗余 UI） */
  hideBrand?: boolean;
  /** 当前语言；不传时不渲染语言切换器（旧 payment-intent 流） */
  lang?: CheckoutLang;
  /** 语言变化回调 */
  onLangChange?: (lang: CheckoutLang) => void;
  /** 当前主题（亮/暗）；用于 data-theme 与明暗按钮图标 */
  theme?: CheckoutTheme;
  /** 主题切换回调 */
  onThemeToggle?: () => void;
}

const CheckoutShell: React.FC<CheckoutShellProps> = ({
  children,
  merchantName,
  logoUrl,
  hideBrand = false,
  lang,
  onLangChange,
  theme = 'light',
  onThemeToggle,
}) => {
  // 文案语言：未接入 i18n（旧流）时回退中文外壳文案；接入时用当前 lang。
  const L: CheckoutLang = lang || 'zh';
  // 是否展示右上角控件区：有任一切换回调即展示。
  const showControls = !!onLangChange || !!onThemeToggle;

  return (
    <div className="fs-checkout" data-theme={theme}>
      {/* 右上角轻量控件区：语言切换 + 明暗切换（样式克制，贴合主题） */}
      {showControls && (
        <div className="fs-controls">
          {onLangChange && lang && (
            <select
              className="fs-lang-select"
              value={lang}
              onChange={(e) => onLangChange(e.target.value as CheckoutLang)}
              aria-label={t('lang_switch_aria', L)}
            >
              {LANG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
          {onThemeToggle && (
            <button
              type="button"
              className="fs-theme-btn"
              onClick={onThemeToggle}
              aria-label={t(theme === 'dark' ? 'theme_toggle_to_light' : 'theme_toggle_to_dark', L)}
              title={t(theme === 'dark' ? 'theme_toggle_to_light' : 'theme_toggle_to_dark', L)}
            >
              {theme === 'dark' ? <BulbFilled /> : <BulbOutlined />}
            </button>
          )}
        </div>
      )}

      {!hideBrand && (
        <div className="fs-brand">
          <div className="fs-brand-row">
            {logoUrl ? (
              <img className="fs-brand-logo" src={logoUrl} alt={merchantName || 'FastStar'} />
            ) : (
              <>
                <span className="fs-brand-mark">F</span>
                <span className="fs-brand-name">FastStar</span>
              </>
            )}
          </div>
          <div className="fs-brand-secure">
            <LockOutlined /> {t('shell_secure', L)}
          </div>
          {merchantName && (
            <div className="fs-brand-provider">{t('shell_provided_by', L, { name: merchantName })}</div>
          )}
        </div>
      )}

      {children}

      {!hideBrand && (
        <div className="fs-page-footer">FastStar {t('shell_footer', L)}</div>
      )}
    </div>
  );
};

export default CheckoutShell;
